import { __decorate } from "tslib";
import Vue from 'vue';
import Component from "vue-class-component";
import { database } from "@/plugins/firebase";
import { callFn } from "@/utils/functionsClient";
import Snackbar from "@/components/Snackbar.vue";
const STATUS_TEXTS = {
    pending: 'Čeká se na terminál...',
    claimed: 'Terminál zpracovává platbu...',
};
/** Shown when the terminal already claimed the request, so cancelling it is no longer possible. */
const CANCEL_WARNING = 'Platbu už nelze zrušit, terminál ji může přesto zpracovat. '
    + 'Nechte okno otevřené: pokud platba na terminálu projde, doklad se vystaví automaticky. '
    + 'Pokud ji zrušíte na terminálu, zavřete okno tlačítkem Rozumím.';
const TIMEOUT_WARNING = 'Terminál neodpověděl. Zkontrolujte na terminálu, zda platba neproběhla, '
    + 'a podle toho ji zopakujte nebo stornujte.';
/** Mirrors `TTL_MS` in `functions/src/payments/requests.ts`, which stamps the request's `expiresAt`. */
const REQUEST_TTL_MS = 5 * 60 * 1000;
/**
 * Browser time cannot be trusted against a server timestamp - a skewed clock would overflow
 * `setTimeout` and fire at once - so `expiresAt` may only shorten the wait, never lengthen it.
 * Without a usable deadline the dialog waits the whole TTL from the moment the timer is armed.
 */
function deadlineDelay(request) {
    const remaining = request.expiresAt ? request.expiresAt.toMillis() - Date.now() : 0;
    return remaining > 0 && remaining < REQUEST_TTL_MS ? remaining : REQUEST_TTL_MS;
}
let CardPaymentDialog = class CardPaymentDialog extends Vue {
    dialog = false;
    amount = 0;
    requestId = '';
    status = 'pending';
    warning = '';
    cancelling = false;
    unsubscribe = null;
    deadlineTimer = null;
    created() {
        document.addEventListener('openCardPaymentDialog', this.openDialog);
    }
    beforeDestroy() {
        document.removeEventListener('openCardPaymentDialog', this.openDialog);
        this.stopWatching();
    }
    openDialog(e) {
        this.amount = e.detail.amount;
        this.requestId = e.detail.requestId;
        this.status = 'pending';
        this.warning = '';
        this.dialog = true;
        this.watchRequest(this.requestId);
    }
    watchRequest(requestId) {
        this.stopWatching();
        this.unsubscribe = database.collection('paymentRequests').doc(requestId).onSnapshot(doc => {
            const request = doc.data();
            if (!request) {
                return;
            }
            this.status = request.status;
            this.watchDeadline(request);
            // A payment that goes through after the operator gave up still gets its receipt.
            if (request.status === 'done') {
                this.close(true);
                return;
            }
            if (request.status === 'failed' || request.status === 'expired') {
                Snackbar.error(request.result?.error ?? 'Platba kartou se nezdařila.');
                this.close(false);
            }
        }, error => {
            console.error(error);
            Snackbar.error('Stav platby se nepodařilo načíst.');
            this.close(false);
        });
    }
    /** Nothing sets `expired` while the agent is offline, so the dialog times the request out itself. */
    watchDeadline(request) {
        if (this.deadlineTimer !== null) {
            return;
        }
        this.deadlineTimer = window.setTimeout(() => this.warn(TIMEOUT_WARNING), deadlineDelay(request));
    }
    warn(text) {
        if (this.dialog) {
            this.warning = text;
        }
    }
    /**
     * The server cancels the request only while the terminal has not claimed it; once it has,
     * the dialog keeps watching and only the operator's acknowledgement closes it.
     */
    async cancel() {
        if (this.cancelling)
            return;
        this.cancelling = true;
        try {
            const result = await callFn('cancelCardPayment', {
                requestId: this.requestId,
            });
            if (result.status === 'claimed') {
                this.warn(CANCEL_WARNING);
                return;
            }
            // The card was already charged, so the payment is finished instead of thrown away.
            this.close(result.status === 'done');
        }
        catch (e) {
            console.error(e);
            this.warn(CANCEL_WARNING);
        }
        finally {
            this.cancelling = false;
        }
    }
    stopWatching() {
        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }
        if (this.deadlineTimer !== null) {
            window.clearTimeout(this.deadlineTimer);
            this.deadlineTimer = null;
        }
    }
    close(success) {
        this.stopWatching();
        this.dialog = false;
        document.dispatchEvent(new CustomEvent('closeCardPaymentDialog', {
            detail: { success },
        }));
    }
    get statusText() {
        return STATUS_TEXTS[this.status] ?? '';
    }
};
CardPaymentDialog = __decorate([
    Component
], CardPaymentDialog);
export default CardPaymentDialog;
